import {
  Building2,
  Home as HomeIcon,
  Dumbbell,
  CalendarHeart,
  Mountain,
  Megaphone,
  Package,
  Stethoscope,
} from "lucide-react";
import { IMAGES } from "@/config/site";
import { useSeo } from "@/lib/seo";
import { PageHero } from "@/components/common/PageHero";
import { Section, SectionHeading } from "@/components/common/Section";
import { StaggerGroup, StaggerItem } from "@/components/common/Reveal";
import { ProtoForm } from "@/components/forms/ProtoForm";
import { partnershipFields } from "@/components/forms/formSchemas";
import { CollectiveEndorsement } from "@/components/common/CollectiveEndorsement";

const TYPES = [
  { icon: Building2, title: "Corporate Relationships", text: "Workplace wellness partnerships with employers of all sizes." },
  { icon: HomeIcon, title: "Apartment & Residential", text: "Resident-wellness experiences for communities and properties." },
  { icon: Dumbbell, title: "Fitness & Wellness", text: "Collaborations with studios, gyms and wellness brands." },
  { icon: CalendarHeart, title: "Event Collaborations", text: "Massage moments for events, launches and activations." },
  { icon: Mountain, title: "Retreats", text: "Restorative bodywork for retreats and getaways." },
  { icon: Megaphone, title: "Brand Ambassadors", text: "Aligned voices who share the Legacy Spa standard." },
  { icon: Package, title: "Vendor Relationships", text: "Trusted suppliers and service partners." },
  { icon: Stethoscope, title: "Healthcare & Wellness Referrals", text: "Referral relationships across the wellness ecosystem." },
];

export default function Partnerships() {
  useSeo({
    title: "Partnerships | Legacy Spa Houston",
    description:
      "Partner with Legacy Spa — corporate, residential, fitness, events, retreats, brand ambassador, vendor and wellness-referral relationships in Greater Houston.",
    path: "/partnerships",
  });

  return (
    <>
      <PageHero
        eyebrow="Partnerships"
        titleLines={["Let's build", "something lasting."]}
        lead="Legacy Spa collaborates with organizations and brands that share a commitment to wellness and quality."
        image={IMAGES.office}
        imageAlt="A modern collaborative workspace."
      />

      <Section className="bg-legacy-charcoal">
        <SectionHeading
          eyebrow="Ways to Partner"
          title="Opportunities across the ecosystem."
          as="h2"
        />
        <StaggerGroup className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {TYPES.map((t) => (
            <StaggerItem key={t.title}>
              <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-legacy-mint/50">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-legacy-mint/10 text-legacy-mint">
                  <t.icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-xl text-white">{t.title}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{t.text}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </Section>

      <section className="bg-legacy-ivory py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-4">
              <SectionHeading
                eyebrow="Start a Conversation"
                title="Propose a partnership."
                tone="light"
                as="h2"
                lead="Share your idea and we'll be in touch to explore how we can work together."
              />
              <CollectiveEndorsement tone="light" className="mt-10" />
            </div>
            <div className="lg:col-span-8">
              <ProtoForm
                formId="partnership"
                endpoint="partnership"
                smsOptIn
                testId="partnership-form"
                tone="light"
                fields={partnershipFields}
                submitLabel="Submit Partnership Inquiry"
                consentLabel="I consent to Legacy Spa contacting me about this partnership inquiry."
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
